import { styled, keyframes } from '@mui/material/styles';
import { Box, Typography, TextareaAutosize } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import CommonText from '../../../../form-components/CommonText';

// ============================================
// Animations
// ============================================
export const pulseError = keyframes`
  0% {
    box-shadow: 0 0 0 0 rgba(211, 47, 47, 0.4);
  }
  50% {
    box-shadow: 0 0 0 6px rgba(211, 47, 47, 0);
  }
  100% {
    box-shadow: 0 0 0 0 rgba(211, 47, 47, 0);
  }
`;

// ============================================
// Validation Highlight
// ============================================
export const ErrorHighlightWrapper = styled(Box, {
  shouldForwardProp: prop => prop !== 'hasError',
})(({ hasError }) => ({
  borderRadius: '8px',
  ...(hasError && {
    animation: `${pulseError} 1.5s ease-in-out infinite`,
    '& .MuiOutlinedInput-notchedOutline': {
      borderColor: '#d32f2f !important',
      borderWidth: '2px !important',
    },
  }),
}));

// ============================================
// Inputs
// ============================================
export const StyledTextarea = styled(TextareaAutosize)({
  width: '400px',
  padding: '15px',
  borderRadius: '4px',
  height: '110px',
  borderColor: 'rgba(133, 133, 133, 0.5)',
  '&:focus': {
    outline: 'none',
    borderColor: '#4AB3BC',
    borderWidth: '2px',
  },
});

// ============================================
// Modal Frame
// ============================================
export const ModalContainer = styled(Box)({
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: '700px',
  maxHeight: '90vh',
  backgroundColor: '#fff',
  borderRadius: '10px',
  border: 'none',
  display: 'flex',
  flexDirection: 'column',
});

export const ModalHeader = styled(Box)({
  backgroundColor: '#111',
  height: '52px',
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  borderTopLeftRadius: '10px',
  borderTopRightRadius: '10px',
  padding: '17px 20px',
  flexShrink: 0,
});

export const ModalCloseIcon = styled(CloseIcon)({
  color: '#fff',
  cursor: 'pointer',
});

// ============================================
// Footer
// ============================================
export const FooterBar = styled(Box)({
  borderTop: '1px solid #e1e1e1',
  padding: '15px 20px',
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  flexShrink: 0,
  backgroundColor: '#fff',
  borderBottomLeftRadius: '10px',
  borderBottomRightRadius: '10px',
});

export const FooterRightGroup = styled(Box)({
  display: 'flex',
  gap: '16px',
});

// ============================================
// Lesson Type Tab
// ============================================
export const LessonTypeBar = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  width: '100%',
  minHeight: '50px',
  padding: '16px 20px 8px',
  flexShrink: 0,
  borderBottom: '1px solid #f0f0f0',
  backgroundColor: '#fff',
});

export const LessonTypeBarLabel = styled(CommonText)({
  marginLeft: '20px',
  minWidth: '100px',
  fontWeight: 500,
});

export const LessonTypeChipGroup = styled(Box)({
  display: 'flex',
  gap: '8px',
});

export const LessonTypeChip = styled(Box, {
  shouldForwardProp: prop => prop !== 'isActive',
})(({ isActive }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '8px 20px',
  borderRadius: '20px',
  cursor: 'pointer',
  border: isActive ? '2px solid #4AB3BC' : '1px solid #d9d9d9',
  backgroundColor: isActive ? '#E8F5F5' : '#fff',
  transition: 'all 0.2s ease',
  '&:hover': {
    borderColor: '#4AB3BC',
    backgroundColor: '#F5FAFA',
  },
}));

export const LessonTypeChipText = styled(Typography, {
  shouldForwardProp: prop => prop !== 'isActive',
})(({ isActive }) => ({
  fontSize: '14px',
  fontWeight: isActive ? 600 : 400,
  color: isActive ? '#4AB3BC' : '#666',
}));

// ============================================
// Content Area
// ============================================
export const ContentArea = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  // justifyContent: 'center' 금지 — 스크롤 영역에서 센터링하면 넘친 콘텐츠 상단이
  // 스크롤 범위 밖으로 잘려 레슨구분 바에 가려짐 (flexbox overflow 클리핑)
  alignItems: 'center',
  padding: '20px',
  overflowY: 'auto',
  flex: 1,
  // flex 자식은 기본 min-height:auto 라 내용만큼 늘어나 스크롤이 안 생긴다 — 0 으로 풀어 모달 안에서 스크롤
  minHeight: 0,
});

export const ContentInner = styled(Box)({
  width: '100%',
  paddingTop: '10px',
});

// ============================================
// Section Card (회색 배경 박스)
// ============================================
export const SectionCard = styled(Box)({
  backgroundColor: '#f8f9fa',
  borderRadius: '8px',
  padding: '16px',
  marginBottom: '12px',
  '&:last-child': {
    marginBottom: 0,
  },
});

export const SectionHeader = styled(CommonText)({
  marginBottom: '12px',
  borderBottom: '1px solid #e0e0e0',
  paddingBottom: '8px',
});

// ============================================
// Form Row & Label
// ============================================
export const FormRow = styled(Box, {
  shouldForwardProp: prop => prop !== 'alignTop' && prop !== 'mb',
})(({ alignTop, mb = 10 }) => ({
  display: 'flex',
  alignItems: alignTop ? 'flex-start' : 'center',
  marginBottom: `${mb}px`,
}));

export const FormLabel = styled(Typography, {
  shouldForwardProp: prop => prop !== 'alignTop',
})(({ alignTop }) => ({
  minWidth: '80px',
  fontSize: '13px',
  color: '#555',
  ...(alignTop && { paddingTop: '8px' }),
}));

export const RequiredMark = styled('span')({
  color: '#d32f2f',
});

// ============================================
// Member Chip
// ============================================
export const SelectedMembersRow = styled(Box, {
  shouldForwardProp: prop => prop !== 'mb',
})(({ mb = 0 }) => ({
  display: 'flex',
  gap: '8px',
  marginLeft: '80px',
  marginBottom: `${mb}px`,
}));

export const MemberChip = styled(Typography, {
  shouldForwardProp: prop => prop !== 'isCounsel',
})(({ isCounsel }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  backgroundColor: isCounsel ? '#4AB3BC' : '#0F7F85',
  color: '#fff',
  borderRadius: '14px',
  padding: '3px 10px',
  fontSize: '12px',
  gap: '5px',
}));

export const MemberChipClose = styled(CloseIcon)({
  fontSize: '13px',
  cursor: 'pointer',
  '&:hover': { opacity: 0.7 },
});

// ============================================
// Time Selectors
// ============================================
export const TimeRow = styled(Box)({
  display: 'flex',
  alignItems: 'center',
});

export const TimeColon = styled(Typography)({
  marginRight: '5px',
});

export const TimeTilde = styled(Typography)({
  marginRight: '10px',
  color: '#666',
});

// ============================================
// Two Column Grid
// ============================================
export const TwoColumnRow = styled(Box)({
  display: 'grid',
  gridTemplateColumns: '1fr 1fr',
  gap: '10px',
});
